import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Define the MRU item type
export interface MRUItem {
  id: string;
  type: string;
  value: string;
  description?: string;
  lastUsed: number;
  isPinned: boolean;
}

// Define the callback registration type
type CallbackRegistration = {
  componentId: string;
  type: string;
  callback: (items: MRUItem[]) => void;
  pathname?: string; // Optional pathname for routing-specific callbacks
};

// Define the MRU context type
export interface AppMRUContextType {
  items: MRUItem[];
  pinnedItems: MRUItem[];
  recentItems: MRUItem[];
  addItem: (newItem: Omit<MRUItem, 'lastUsed' | 'isPinned'>) => void;
  pinItem: (id: string) => void;
  unpinItem: (id: string) => void;
  removeItem: (id: string) => void;
  registerCallback: (type: string, callback: (items: MRUItem[]) => void, pathname?: string) => void;
  unregisterCallback: (type: string) => void;
  isCallbackRegistered: (type: string) => boolean;
  executeCallback: (items: MRUItem | MRUItem[]) => void; // Accepts either a single item or an array of items
  queueItem: (item: MRUItem) => void;
  dequeueItem: (id: string) => void; // Remove an item from the queue by ID
  queuedItems: MRUItem[];
  isDrawerOpen: boolean;
  toggleDrawer: () => void;
  closeDrawer: () => void;
  shouldRender: boolean; // Whether MRU components should render based on environment
}

// Export the context so it can be imported directly
export const AppMRUContext = createContext<AppMRUContextType | undefined>(undefined);

// Fixed key for MRU items in local storage (no user-specific prefix)
const APP_MRU_STORAGE_KEY = 'appMruItems_demo';
const MAX_MRU_ITEMS = 20;

export const AppMRUProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Context is simplified for demo purposes - no user ID dependency

  const [items, setItems] = useState<MRUItem[]>([]);
  const [queuedItems, setQueuedItems] = useState<MRUItem[]>([]);
  const [registeredCallbacks, setRegisteredCallbacks] = useState<CallbackRegistration[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Determine if MRU components should render based on environment
  const shouldRender = useMemo(() => {
    // console.log('Checking if MRU should render:', import.meta.env.VITE_STAGE);

    return ['local', 'dev', 'staging'].includes(import.meta.env.VITE_STAGE);
  }, []);

  // Load items from localStorage when drawer is opened - simplified for demo
  const loadItemsFromStorage = useCallback(() => {
    try {
      // console.log('Loading MRU items from storage');
      const storedItemsString = localStorage.getItem(APP_MRU_STORAGE_KEY);

      if (storedItemsString) {
        try {
          const storedItems = JSON.parse(storedItemsString);
          // console.log(
          //   'Found stored MRU items, count:',
          //   Array.isArray(storedItems) ? storedItems.length : 'not an array'
          // );
          setItems(Array.isArray(storedItems) ? storedItems : []);
        } catch (parseError) {
          console.error('Error parsing stored MRU items:', parseError);
          setItems([]);
        }
      } else {
        // console.log('No stored MRU items found');

        setItems([]);
      }

      // Mark as initialized
      setInitialized(true);
    } catch (error) {
      console.error('Error loading MRU items from localStorage:', error);
      setItems([]);
      setInitialized(true); // Still mark as initialized so we don't get stuck
    }
  }, []);

  // Monitor user ID and initialize when it becomes available
  useEffect(() => {
    if (initialized) {
      return;
    }

    // Initialize regardless of user ID for demo purposes
    loadItemsFromStorage();
  }, [initialized, loadItemsFromStorage]);

  // Save items to localStorage - simplified for demo
  const saveItemsToStorage = useCallback(() => {
    try {
      // console.log(`Saving ${items.length} MRU items to storage`);
      localStorage.setItem(APP_MRU_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving MRU items to localStorage:', error);
    }
  }, [items]);

  // Save items when they change, but only if we're initialized
  useEffect(() => {
    if (initialized) {
      saveItemsToStorage();
    }
  }, [items, initialized, saveItemsToStorage]);

  // This useEffect has been removed to prevent automatic processing of queued items
  // Items will now only be processed when executeCallback is explicitly called
  // with the queuedItems array or when the user clicks the "Process Queue" button

  // Filter items into pinned and recent
  const pinnedItems = useMemo(() => {
    return items.filter(item => item.isPinned).sort((a, b) => b.lastUsed - a.lastUsed);
  }, [items]);

  const recentItems = useMemo(() => {
    return items
      .filter(item => !item.isPinned)
      .sort((a, b) => b.lastUsed - a.lastUsed)
      .slice(0, MAX_MRU_ITEMS);
  }, [items]);

  // Add or update an item in the MRU list
  const addItem = useCallback(
    (newItem: Omit<MRUItem, 'lastUsed' | 'isPinned'>) => {
      // For demo, just initialize directly if not already initialized
      if (!initialized) {
        loadItemsFromStorage();

        return; // Wait for initialization to complete
      }

      setItems(prevItems => {
        const now = Date.now();
        const existingItemIndex = prevItems.findIndex(item => item.id === newItem.id);

        if (existingItemIndex >= 0) {
          // Update existing item's timestamp
          const updatedItems = [...prevItems];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            lastUsed: now,
          };

          return updatedItems;
        } else {
          // Add new item
          const fullItem: MRUItem = {
            ...newItem,
            lastUsed: now,
            isPinned: false,
          };

          // If we're at the limit, remove the oldest non-pinned item
          if (prevItems.filter(item => !item.isPinned).length >= MAX_MRU_ITEMS) {
            const nonPinnedItems = prevItems.filter(item => !item.isPinned);
            const oldestItem = nonPinnedItems.reduce(
              (oldest, item) => (item.lastUsed < oldest.lastUsed ? item : oldest),
              nonPinnedItems[0]
            );

            return [...prevItems.filter(item => item.id !== oldestItem.id), fullItem];
          }

          return [...prevItems, fullItem];
        }
      });
    },
    [initialized, loadItemsFromStorage]
  );

  // Pin an item
  const pinItem = useCallback((id: string) => {
    setItems(prevItems =>
      prevItems.map(item => (item.id === id ? { ...item, isPinned: true } : item))
    );
  }, []);

  // Unpin an item
  const unpinItem = useCallback((id: string) => {
    setItems(prevItems =>
      prevItems.map(item => (item.id === id ? { ...item, isPinned: false } : item))
    );
  }, []);

  // Remove an item
  const removeItem = useCallback((id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  }, []);

  // Register a callback for a specific item type
  const registerCallback = useCallback(
    (type: string, callback: (items: MRUItem[]) => void, pathname?: string) => {
      // Create a registration object with the callback, component ID, and normalized pathname
      const registration: CallbackRegistration = {
        componentId: 'demo-component', // For demo purposes, use a fixed component ID
        type,
        callback,
        pathname,
      };

      // console.log(`Registering callback for type: ${type}`);

      // Remove any existing callback for this type before adding the new one
      setRegisteredCallbacks(prev => [...prev.filter(cb => cb.type !== type), registration]);
    },
    []
  );

  // Unregister a callback for a specific type
  const unregisterCallback = useCallback((type: string) => {
    setRegisteredCallbacks(prev => prev.filter(reg => reg.type !== type));
  }, []);

  // Check if a callback is registered for a specific item type
  const isCallbackRegistered = useCallback(
    (type: string) => {
      return registeredCallbacks.some(reg => reg.type === type);
    },
    [registeredCallbacks]
  );

  // Execute a callback for an array of items or a single item
  const executeCallback = useCallback(
    (itemsToProcess: MRUItem | MRUItem[]) => {
      // Convert single item to array if needed
      const itemsArray = Array.isArray(itemsToProcess) ? itemsToProcess : [itemsToProcess];

      if (itemsArray.length === 0) {
        return;
      }

      // Group items by type to call appropriate callbacks
      const itemsByType = itemsArray.reduce<Record<string, MRUItem[]>>((acc, item) => {
        if (!acc[item.type]) {
          acc[item.type] = [];
        }

        acc[item.type].push(item);

        return acc;
      }, {});

      // Process each type group
      Object.entries(itemsByType).forEach(([type, typeItems]) => {
        const registration = registeredCallbacks.find(cb => cb.type === type);

        if (!registration) {
          console.warn(`No callback registered for type: ${type}`);

          return;
        }

        // Check if the callback is valid for the current pathname
        if (
          registration.pathname !== window.location.pathname &&
          registration.pathname !== '*' &&
          registration.pathname !== undefined
        ) {
          console.warn(
            `Callback for type ${type} is not valid for the current pathname: ${window.location.pathname}`
          );

          return;
        }

        // Execute the callback with the array of items of this type
        registration.callback(typeItems);

        // If these items were from the queue, remove them from the queue
        const itemIds = typeItems.map(item => item.id);
        setQueuedItems(prev => prev.filter(item => !itemIds.includes(item.id)));
      });

      // Close the drawer
      setIsDrawerOpen(false);
    },
    [registeredCallbacks]
  );

  // Queue an item for processing
  const queueItem = useCallback((item: MRUItem) => {
    setQueuedItems(prevQueue => {
      // Check if already in queue
      if (prevQueue.some(qItem => qItem.id === item.id)) {
        return prevQueue;
      }

      return [...prevQueue, item];
    });
  }, []);

  // Remove an item from the queue
  const dequeueItem = useCallback((id: string) => {
    setQueuedItems(prevQueue => prevQueue.filter(item => item.id !== id));
  }, []);

  // Toggle drawer open/closed
  const toggleDrawer = useCallback(() => {
    // When opening the drawer, ensure items are loaded
    if (!isDrawerOpen && !initialized) {
      loadItemsFromStorage();
    }

    setIsDrawerOpen(prev => !prev);

    // Clear queued items when drawer is closed
    if (isDrawerOpen) {
      setQueuedItems([]);
    }
  }, [isDrawerOpen, initialized, loadItemsFromStorage]);

  // Close drawer
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue: AppMRUContextType = useMemo(
    () => ({
      items,
      pinnedItems,
      recentItems,
      addItem,
      pinItem,
      unpinItem,
      removeItem,
      registerCallback,
      unregisterCallback,
      isCallbackRegistered,
      executeCallback,
      queueItem,
      dequeueItem,
      queuedItems,
      isDrawerOpen,
      toggleDrawer,
      closeDrawer,
      shouldRender,
    }),
    [
      items,
      pinnedItems,
      recentItems,
      addItem,
      pinItem,
      unpinItem,
      removeItem,
      registerCallback,
      unregisterCallback,
      isCallbackRegistered,
      executeCallback,
      queueItem,
      dequeueItem,
      queuedItems,
      isDrawerOpen,
      toggleDrawer,
      closeDrawer,
      shouldRender,
    ]
  );

  return <AppMRUContext.Provider value={contextValue}>{children}</AppMRUContext.Provider>;
};

// Export a hook for consuming the MRU context
export const useAppMRU = (): AppMRUContextType => {
  const context = useContext(AppMRUContext);

  if (context === undefined) {
    throw new Error('useAppMRU must be used within an AppMRUProvider');
  }

  return context;
};

export default AppMRUContext;
